import { Component } from '@theme/component';

/**
 * Per-app hooks. The wrapper is the strip that slides; the container is the
 * window it slides behind. Both class names, and both globals, were read off
 * the arrows the apps render for themselves.
 */
const FEEDS = {
  instafeed: {
    wrapper: '.instafeed-new-layout-wrapper',
    container: '.instafeed-new-layout-container',
    move: (direction, index) => window.InstafeedAPI?.sliderMove?.(direction, index),
  },
  tiktok: {
    wrapper: '.juniper-new-carousel-wrapper',
    container: '.juniper-new-carousel-container',
    move: (direction, index) => window.juniperSliderMove?.(direction, index),
  },

  /* The theme's own carousel. Nothing global to call and nothing to hunt for
   * on the page either: the strip belongs to the same section as these
   * buttons, so it is looked up there and not in the document. It is a real
   * scroller already, which is the path everything below prefers anyway. */
  carousel: {
    wrapper: '.carousel-content slideshow-slides',
    container: '.carousel-content slideshow-slides',
    scope: 'section',
  },

  /* A `product-list` set to carousel (the homepage New in, Figma 699:72). Same
   * reasoning as above: the strip is a real scroller in the same section.
   *
   * `settleAtEdges`: twelve cards with a peek leave a remainder, so stepping one
   * card at a time ended in a 185px click whose only job was to finish showing
   * the last card (measured at 1440: 7 x 310, then 185). With it, a step that
   * would leave less than LEFTOVER of a card goes straight to the edge. Opt-in
   * so the app feeds and the theme carousel keep stepping exactly as before. */
  product_list: {
    wrapper: '.resource-list__carousel slideshow-slides',
    container: '.resource-list__carousel slideshow-slides',
    scope: 'section',
    settleAtEdges: true,
  },
};

/** How far off the end still counts as the end, in pixels. */
const EDGE_TOLERANCE = 2;

/** The share of a card below which a last step is folded into the one before. */
const LEFTOVER = 0.75;

/** How long to keep looking for the app's grid before giving up. */
const WAIT_INTERVAL = 200;
const WAIT_ATTEMPTS = 25;

/**
 * Drives the slider of an Instafeed or a Mintt TikTok Feed grid from a pair of
 * buttons the theme owns, so the arrows can carry the design's glyph and sit on
 * the heading line instead of floating over the grid where the app puts them.
 *
 * Neither global is a documented API. If an app update renames or removes one,
 * the buttons stop moving that grid and nothing else on the page is affected:
 * the call is optional-chained, so a missing global is a no-op rather than a
 * thrown error that would take the rest of the page's JavaScript with it.
 *
 * The app tells nobody where its slider is, so the position is read back off
 * the translateX the app writes onto the wrapper, and the buttons are disabled
 * at each end. A MutationObserver watches that inline style, which means the
 * state also keeps up when the grid is moved by something other than these
 * buttons -- a swipe, or a resize that reflows the items.
 *
 * Below 750 none of that applies: the stylesheet turns the container into a
 * real scroller and neutralises the transform, because the app's step was
 * computed from an item width the design overrides. There the buttons scroll
 * the container by one card and the position is simply scrollLeft. Which mode
 * applies is read off the element, not off a media query, so the two never
 * disagree about where the breakpoint is.
 *
 * @extends {Component}
 */
class MmSocialArrowsComponent extends Component {
  requiredRefs = ['previous', 'next'];

  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  /** @type {HTMLElement | null | undefined} */
  #scroller;

  #onScroll = () => this.#syncEdges();

  connectedCallback() {
    super.connectedCallback();
    this.#watch();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#observer?.disconnect();
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    clearTimeout(this.#timer);
  }

  /**
   * Both apps fetch their posts and build the grid well after this module runs,
   * so on the first pass there is nothing to observe yet. Measured: bailing out
   * once left the arrows permanently enabled. Keeps looking for a few seconds
   * and then stops, rather than polling for the life of the page.
   *
   * @param {number} attempt
   */
  #watch(attempt = 0) {
    const wrapper = this.#wrapper;

    if (!wrapper) {
      if (attempt < WAIT_ATTEMPTS) {
        this.#timer = setTimeout(() => this.#watch(attempt + 1), WAIT_INTERVAL);
      }
      return;
    }

    // The app writes the slide position as an inline transform, and rewrites
    // every item's width on resize, so this fires for both.
    this.#observer = new MutationObserver(() => this.#syncEdges());
    this.#observer.observe(wrapper, { attributes: true, attributeFilter: ['style'] });

    // And when the strip is a scroller, nothing touches that style: a swipe
    // only moves scrollLeft.
    this.#scroller = this.#container;
    this.#scroller?.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#syncEdges();
  }

  previous() {
    this.#move(-1);
  }

  next() {
    this.#move(1);
  }

  get #feed() {
    return FEEDS[this.getAttribute('feed') ?? ''];
  }

  /** Where a feed's selectors are resolved: the page, or this band alone. */
  get #root() {
    const feed = this.#feed;
    if (feed?.scope === 'section') return this.closest('.shopify-section') ?? document;
    return document;
  }

  get #wrapper() {
    const feed = this.#feed;
    return feed ? this.#root.querySelector(feed.wrapper) : null;
  }

  get #container() {
    const feed = this.#feed;
    return feed ? this.#root.querySelector(feed.container) : null;
  }

  /**
   * Whether the strip scrolls instead of sliding. True only while the phone
   * stylesheet has released the container's overflow.
   *
   * @param {Element | null} container
   * @returns {boolean}
   */
  #scrolls(container) {
    if (!(container instanceof HTMLElement)) return false;
    return (
      getComputedStyle(container).overflowX !== 'hidden' &&
      container.scrollWidth > container.clientWidth + EDGE_TOLERANCE
    );
  }

  /**
   * One card and the gap after it, measured rather than assumed: the apps
   * write the item width themselves and the design overrides it.
   *
   * @param {HTMLElement} wrapper
   * @param {HTMLElement} container
   * @returns {number}
   */
  #step(wrapper, container) {
    const item = wrapper.firstElementChild;
    const width = item instanceof HTMLElement ? item.getBoundingClientRect().width : 0;
    const gap = parseFloat(getComputedStyle(wrapper).columnGap) || 0;
    return width > 0 ? width + gap : container.clientWidth;
  }

  /**
   * @param {number} direction -1 for previous, 1 for next.
   */
  #move(direction) {
    const wrapper = this.#wrapper;
    const container = this.#container;

    if (wrapper instanceof HTMLElement && container instanceof HTMLElement && this.#scrolls(container)) {
      const step = this.#step(wrapper, container);

      if (this.#feed?.settleAtEdges) {
        container.scrollTo({ left: this.#settledTarget(container, direction, step), behavior: 'smooth' });
      } else {
        container.scrollBy({ left: direction * step, behavior: 'smooth' });
      }
      return;
    }

    this.#feed?.move(direction, Number(this.getAttribute('feed-index') ?? 0));
  }

  /**
   * One card along, unless that would strand less than LEFTOVER of a card
   * before the edge. Forward, the short remainder is added to this step. Back,
   * the target is the card boundary a full card behind, so leaving the end does
   * not repeat the short step either; scroll snap settles it on the card.
   *
   * @param {HTMLElement} container
   * @param {number} direction -1 for previous, 1 for next.
   * @param {number} step
   * @returns {number}
   */
  #settledTarget(container, direction, step) {
    const current = container.scrollLeft;
    const max = container.scrollWidth - container.clientWidth;

    if (direction > 0) {
      const target = current + step;
      return max - target < step * LEFTOVER ? max : target;
    }

    let target = Math.floor((current - EDGE_TOLERANCE) / step) * step;
    if (current - target < step * LEFTOVER) target -= step;
    return Math.max(target, 0);
  }

  /** Disables whichever arrow has nowhere left to go. */
  #syncEdges() {
    const wrapper = this.#wrapper;
    const container = this.#container;
    if (!(wrapper instanceof HTMLElement) || !(container instanceof HTMLElement)) return;

    const scrolls = this.#scrolls(container);
    const offset = scrolls ? container.scrollLeft : Math.abs(this.#offset(wrapper));

    const travel = scrolls
      ? container.scrollWidth - container.clientWidth
      : Math.max(wrapper.scrollWidth - container.clientWidth, 0);

    this.#toggle(this.refs.previous, offset <= EDGE_TOLERANCE);
    this.#toggle(this.refs.next, travel <= 0 || offset >= travel - EDGE_TOLERANCE);
  }

  /**
   * Reads the translateX the app writes onto the wrapper.
   * @param {HTMLElement} wrapper
   * @returns {number}
   */
  #offset(wrapper) {
    const matrix = new DOMMatrixReadOnly(getComputedStyle(wrapper).transform);
    return matrix.m41;
  }

  /**
   * @param {Element | Element[] | null | undefined} button
   * @param {boolean} disabled
   */
  #toggle(button, disabled) {
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = disabled;
  }
}

if (!customElements.get('mm-social-arrows-component')) {
  customElements.define('mm-social-arrows-component', MmSocialArrowsComponent);
}
